import { UserIntentModal } from "@/components/auth/UserIntentModal";
import { LanguageSelector } from "@/components/base";
import { PasswordField } from "@/components/base/PasswordField";
import { useTranslation } from "@/hooks/useTranslation";
import { useLogin } from "@/services/auth";
import { currentUserAtom } from "@/store/login";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	Button,
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	Input,
	Typography,
	toast,
} from "@st-marketplace/ui";
import { useNavigate } from "@tanstack/react-router";
import { useAtomValue, useSetAtom } from "jotai";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";

/** Teams deep link to support, with the message pre-filled. */
const teamsChat = (message: string) =>
	`https://teams.microsoft.com/l/chat/0/0?users=mani_nikou@yahoo.com&message=${encodeURIComponent(
		message,
	)}`;

const SIGNUP_CHAT_URL = teamsChat("I want to register");
const RESET_CHAT_URL = teamsChat("I want to reset my password");

const signInSchema = z.object({
	username: z.string().min(1),
	password: z.string().min(1),
});

type SignInFormData = z.infer<typeof signInSchema>;

export default function SignIn() {
	const { t } = useTranslation();
	const loginMutation = useLogin();
	const loggedInUser = useAtomValue(currentUserAtom);
	const setCurrentUser = useSetAtom(currentUserAtom);
	const navigate = useNavigate();

	useEffect(() => {
		if (loggedInUser && loggedInUser.user_role === "FC_USER") {
			toast.error(t("auth.accessDenied"));
			setCurrentUser(null);
		}
	}, [loggedInUser, setCurrentUser, t]);

	const form = useForm<SignInFormData>({
		resolver: zodResolver(signInSchema),
		defaultValues: { username: "", password: "" },
		mode: "onChange",
	});

	const canSubmit = !loginMutation.isPending;

	const onSubmit = (data: SignInFormData) => {
		loginMutation.mutate(
			{ username: data.username, passwd: data.password },
			{
				onSuccess: (user) => {
					toast.success(t("auth.welcome"));
					if (user.user_role === "ADMIN") {
						navigate({ to: "/st-admin/dashboard" });
					} else {
						navigate({ to: "/dashboard" });
					}
				},
			},
		);
	};

	return (
		<div className="relative grid min-h-screen place-items-center bg-background-0 px-2 py-5 md:px-6 md:py-8">
			<div
				aria-hidden
				className="pointer-events-none absolute inset-0 opacity-20 dark:opacity-20"
				style={{
					background: [
						"radial-gradient(1100px circle at 18% 20%, var(--color-primary-fill), transparent 58%)",
						"radial-gradient(900px circle at 78% 18%, #818cf8, transparent 60%)",
						"radial-gradient(1200px circle at 55% 75%, #00b4ff, transparent 60%)",
					].join(","),
				}}
			/>
			<div
				aria-hidden
				className="pointer-events-none absolute inset-0"
				style={{
					background:
						"radial-gradient(900px circle at 50% 45%, transparent 36%, var(--color-background-0) 100%)",
				}}
			/>

			<div className="relative z-1 w-full max-w-115 overflow-hidden rounded-xl border border-secondary-border bg-background-50 shadow-[0_22px_90px_rgba(0,0,0,0.55)] backdrop-blur-[18px]">
				<div
					aria-hidden
					className="pointer-events-none absolute -inset-px rounded-[inherit] p-px"
					style={{
						background:
							"linear-gradient(135deg, var(--color-primary-fill), rgba(129,140,248,0.15), rgba(255,255,255,0.12))",
						WebkitMask:
							"linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
						WebkitMaskComposite: "xor",
						maskComposite: "exclude",
					}}
				/>

				<div className="flex flex-col gap-3.5 p-3.5 sm:p-5">
					<div className="flex flex-col gap-1.25">
						<div className="flex flex-row items-center justify-between">
							<Typography
								variant="title-lg"
								as="h1"
								className="font-black tracking-tight opacity-80 select-none"
							>
								{t("auth.signin")}
							</Typography>
							<LanguageSelector />
						</div>
						<Typography variant="body-md" className="opacity-80">
							{t("auth.enterCredentials")}
						</Typography>
					</div>

					<hr className="border-secondary-border opacity-20" />

					<Form {...form}>
						<form
							noValidate
							onSubmit={form.handleSubmit(onSubmit)}
							className="flex flex-col gap-2.75 pt-px"
						>
							<FormField
								control={form.control}
								name="username"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>{t("auth.username")}</FormLabel>
										<FormControl>
											<Input
												{...field}
												value={field.value ?? ""}
												ltr
												placeholder={t("auth.username")}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<PasswordField
								name="password"
								label={t("auth.password")}
								control={form.control}
								required
								dir="ltr"
							/>

							<div className="mt-0.5 flex flex-row items-center justify-between">
								<a href={RESET_CHAT_URL} target="_blank" rel="noreferrer">
									<Typography className="cursor-pointer text-primary-100 hover:underline">
										{t("auth.forgotPasswordLink")}
									</Typography>
								</a>
								<a href={SIGNUP_CHAT_URL} target="_blank" rel="noreferrer">
									<Typography className="cursor-pointer text-primary-100 hover:underline">
										{t("auth.signup")}
									</Typography>
								</a>
							</div>

							<Button
								type="submit"
								size="lg"
								loading={!canSubmit}
								disabled={!canSubmit}
								className="relative w-full"
							>
								{t("auth.signin")}
							</Button>

							<Typography
								variant="body-md"
								className="mt-1 text-center opacity-85"
							>
								{t("auth.dontHaveAccount")}{" "}
								<a
									href={SIGNUP_CHAT_URL}
									target="_blank"
									rel="noreferrer"
									className="cursor-pointer text-primary-100 hover:underline"
								>
									{t("auth.signup")}
								</a>
							</Typography>
						</form>
					</Form>
				</div>
			</div>

			<UserIntentModal />
		</div>
	);
}
